/**
 * How a category name is normalized and compared — the two rules shared by
 * every "list of named groups the viewer keeps" on the platform (extracted
 * 2026-09-17 for the saved-prompt library).
 *
 * WHY THIS FILE EXISTS AT ALL. Both rules below are CORRECTNESS, not
 * convenience, and each was written after a real defect:
 *
 *   · the bound is CODE POINTS, not UTF-16 units (2026-09-16). An emoji is two
 *     units and a `slice` landing between them leaves a lone surrogate — valid
 *     JavaScript, invalid UTF-8, rejected by Firestore — so the name failed to
 *     save with nothing shown to the person who typed it.
 *   · the comparison folds case AND accents, so "Clients", "clients" and a
 *     Spanish label with the same letters are one group rather than three
 *     sitting beside each other looking identical.
 *
 * A second, independent copy of those two rules in whichever surface came next
 * is exactly how the emoji bug returns somewhere nobody remembers to test. So
 * they live here once and `types/threadCategories` re-exports them under its
 * own long-standing names: nothing was renamed and no import site churned, the
 * same move `styles/layers.ts` made on 2026-09-11.
 *
 * DELIBERATELY NOT HERE: the built-in sections, the adoption dance, the
 * fold-aliases and the ordered-list parser. Those are about a thread rail's
 * renamable defaults and mean nothing to a list of saved prompts; lifting them
 * would import a concept the next caller does not have.
 *
 * Pure, and Firebase-free by contract — the chat-isolation suites render
 * modules that reach this one, and anything pulling `firebase/auth` into that
 * graph touches global fetch at import time and takes the suite down before a
 * single test runs (2026-09-11).
 */
import { boundByCodePoints } from '../utils/textBounds';

/** The longest a category name may be, in CODE POINTS. */
export const CATEGORY_NAME_MAX = 40;

/** A string, or '' for anything that is not one. */
export function trimToString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * A category name as stored: trimmed, single-spaced, bounded by code points,
 * '' when there is nothing left. See the file header for why the bound counts
 * code points rather than string length.
 */
export function normalizeCategoryName(raw: unknown): string {
  return boundByCodePoints(trimToString(raw).replace(/\s+/g, ' '), CATEGORY_NAME_MAX);
}

/**
 * The key two names are compared by: case- and accent-insensitive, so
 * "Clients" ≡ "clients" ≡ "clientes"'s own key. Never shown to anyone — it is
 * a comparison key, not a label.
 */
export function foldCategoryName(raw: unknown): string {
  return normalizeCategoryName(raw)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}
